import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useTheme } from "../../contexts/ThemeContext";
import {
  saveDeviceToken,
  removeDeviceToken,
  getDeviceToken,
} from "../../utils/deviceToken";

const DeviceTokenSettings: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [token, setToken] = useState("");
  const [savedToken, setSavedToken] = useState("");
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    // Load saved token on mount (automatically restores from cookie if needed)
    const stored = getDeviceToken();
    if (stored) {
      setSavedToken(stored);
      setToken(stored);
    }
  }, []);

  const handleSave = () => {
    if (token.trim()) {
      saveDeviceToken(token.trim());
      setSavedToken(token.trim());
      enqueueSnackbar(
        "Device token saved! You can now access from any IP address.",
        { variant: "success" }
      );
      window.location.reload(); // Reload to apply token
    }
  };

  const handleRemove = () => {
    if (window.confirm("Are you sure you want to remove your device token?")) {
      removeDeviceToken();
      setSavedToken("");
      setToken("");
      enqueueSnackbar("Device token removed.", { variant: "info" });
      window.location.reload();
    }
  };

  return (
    <div
      className={`rounded-lg shadow p-6 ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      }`}
    >
      <div className="mb-6">
        <h2
          className={`text-xl font-bold mb-2 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Device Token
        </h2>
        <p
          className={`text-sm ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
        >
          Use a device token to access the system from any IP address without
          restrictions.
        </p>
      </div>

      {savedToken ? (
        <div className="space-y-4">
          {/* Token Status */}
          <div
            className={`p-4 rounded-lg border-2 ${
              isDarkMode
                ? "bg-green-900/20 border-green-600"
                : "bg-green-50 border-green-500"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="font-semibold text-green-600">
                Device Token Active
              </span>
            </div>
            <p
              className={`text-sm ${
                isDarkMode ? "text-green-400" : "text-green-700"
              }`}
            >
              Your device is authorized to access from any IP address.
            </p>
          </div>

          {/* Show Token */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Current Token
            </label>
            <div className="flex gap-2">
              <input
                type={showToken ? "text" : "password"}
                value={savedToken}
                readOnly
                className={`flex-1 px-4 py-2 border rounded-lg font-mono text-sm ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-gray-50 border-gray-300 text-gray-900"
                }`}
              />
              <button
                onClick={() => setShowToken(!showToken)}
                className={`px-4 py-2 rounded-lg border ${
                  isDarkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                title={showToken ? "Hide" : "Show"}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {showToken ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Remove Button */}
          <button
            onClick={handleRemove}
            className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
          >
            Remove Device Token
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Info Box */}
          <div
            className={`p-4 rounded-lg border ${
              isDarkMode
                ? "bg-blue-900/20 border-blue-600"
                : "bg-blue-50 border-blue-500"
            }`}
          >
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p
                  className={`text-sm font-semibold ${
                    isDarkMode ? "text-blue-400" : "text-blue-700"
                  }`}
                >
                  No device token configured
                </p>
                <p
                  className={`text-sm mt-1 ${
                    isDarkMode ? "text-blue-300" : "text-blue-600"
                  }`}
                >
                  Contact your administrator to get a device token. This will
                  allow you to access the system from any IP address.
                </p>
              </div>
            </div>
          </div>

          {/* Input Token */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Enter Device Token
            </label>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your device token here..."
              className={`w-full px-4 py-2 border rounded-lg font-mono text-sm ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
              }`}
            />
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={!token.trim()}
            className="w-full px-4 py-2 bg-phoenix-600 hover:bg-phoenix-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Device Token
          </button>

          {/* Instructions */}
          <div
            className={`p-4 rounded-lg ${
              isDarkMode ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <h4
              className={`font-semibold mb-2 text-sm ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              How to get a device token:
            </h4>
            <ol
              className={`list-decimal list-inside space-y-1 text-sm ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              <li>Contact your system administrator</li>
              <li>Request a device token for your computer</li>
              <li>Copy the token they provide</li>
              <li>Paste it in the field above and click Save</li>
              <li>The page will reload and you'll have unrestricted access!</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceTokenSettings;
